import { ENCABEZADO_INMOBILIARIA, getInmobiliariaActiva, setInmobiliariaActiva } from './inmobiliaria-activa';
import type { LoginResponse, SessionUser } from '../auth/session-types';
import type {
  AdminUser,
  Advisor,
  CreateAdminInput,
  CatalogUnit,
  CatalogUnitsQuery,
  CatalogValidation,
  Company,
  ConfirmCatalogInput,
  ConfirmCatalogResult,
  CreateAdvisorInput,
  CreateCompanyInput,
  CreateProjectInput,
  Paginated,
  Project,
  UpdateCompanyInput,
  UpdateProjectInput,
} from '../types/admin';
import type {
  AnulacionSeparacion,
  ArchivoDescargado,
  ConsultaAdminParams,
  ConsultaPropiasParams,
  CorreccionAbierta,
  DescarteSeparacion,
  DocumentoSeparacion,
  EnlaceDescarga,
  EnvioCorreo,
  ResultadoIntegridad,
  SeparacionDetalle,
  SeparacionResumen,
} from '../types/consultas';
import type {
  ApplicantInput,
  ApplicantRecord,
  AuthorizationResult,
  AvailableUnit,
  CalculatePlanInput,
  ConditionsInput,
  ConditionsResult,
  CreateSeparationInput,
  GeneratePlanInput,
  InstallmentInput,
  InstallmentsResult,
  IssuedDocument,
  OwnSeparationDetail,
  PaymentPlanPreview,
  SeparationDraftResponse,
} from '../types/separations';

const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const contentType = response.headers.get('content-type');
  const body = contentType?.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message = typeof body === 'object' && body !== null && 'message' in body
      ? String(body.message)
      : 'No fue posible completar la solicitud.';
    throw new ApiError(response.status, message, body);
  }

  return body as T;
}

let accessToken: string | null = null;

let onUnauthorized: (() => void) | undefined;

export const apiClient = {
  setAccessToken(token: string | null): void {
    accessToken = token;
  },

  /** fix-60 · ADR-018: ver `lib/inmobiliaria-activa.ts`. */
  setInmobiliariaActiva(id: string | null): void {
    setInmobiliariaActiva(id);
  },

  setUnauthorizedHandler(handler: (() => void) | undefined): void {
    onUnauthorized = handler;
  },

  async login(correo: string, contrasena: string): Promise<LoginResponse> {
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ correo, contrasena }),
    });
  },

  async me(): Promise<SessionUser> {
    return request<SessionUser>('/auth/me', {
      headers: authorizationHeaders(),
    }).catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) {
        onUnauthorized?.();
      }
      throw error;
    });
  },

  /** Pública: «si el correo existe, te enviamos un enlace» (respuesta idéntica exista o no). */
  async requestPasswordReset(correo: string): Promise<{ message: string }> {
    return request<{ message: string }>('/auth/recuperar', {
      method: 'POST',
      body: JSON.stringify({ correo }),
    });
  },

  /** Pública: define la contraseña con el token de un solo uso (invitación o recuperación). */
  async setPassword(token: string, contrasena: string): Promise<{ message: string }> {
    return request<{ message: string }>('/auth/definir-contrasena', {
      method: 'POST',
      body: JSON.stringify({ token, contrasena }),
    });
  },

  async logout(): Promise<{ message: string }> {
    return request<{ message: string }>('/auth/logout', {
      method: 'POST',
      headers: authorizationHeaders(),
    });
  },

  companies: {
    list(params?: { page?: number; pageSize?: number }): Promise<Paginated<Company>> {
      return request<Paginated<Company>>(`/real-estate-companies${buildQuery(params)}`, {
        headers: authorizationHeaders(),
      });
    },
    get(id: string): Promise<Company> {
      return request<Company>(`/real-estate-companies/${id}`, { headers: authorizationHeaders() });
    },
    create(data: CreateCompanyInput): Promise<Company> {
      return request<Company>('/real-estate-companies', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: authorizationHeaders(),
      });
    },
    update(id: string, data: UpdateCompanyInput): Promise<Company> {
      return request<Company>(`/real-estate-companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: authorizationHeaders(),
      });
    },
    deactivate(id: string): Promise<{ activa: false }> {
      return request<{ activa: false }>(`/real-estate-companies/${id}`, {
        method: 'DELETE',
        headers: authorizationHeaders(),
      });
    },
    /** feat-66: sube o reemplaza el logo (multipart, campo `logo`). */
    uploadLogo(id: string, archivo: File): Promise<Company> {
      const form = new FormData();
      form.append('logo', archivo);
      return requestForm<Company>(`/real-estate-companies/${id}/logo`, form, authorizationHeaders(), 'PUT');
    },
    removeLogo(id: string): Promise<Company> {
      return request<Company>(`/real-estate-companies/${id}/logo`, {
        method: 'DELETE',
        headers: authorizationHeaders(),
      });
    },
    /** Bytes del logo con la sesión (no es una URL pública): se muestran como object URL. */
    logo(id: string): Promise<Blob> {
      return requestBlob(`/real-estate-companies/${id}/logo`);
    },
  },

  projects: {
    /** Proyectos ACTIVO del tenant; para el ASESOR, solo los asignados (`GET /projects/available`). */
    available(): Promise<Project[]> {
      return request<Project[]>('/projects/available', { headers: authorizationHeaders() });
    },
    list(params?: { page?: number; pageSize?: number }): Promise<Paginated<Project>> {
      return request<Paginated<Project>>(`/projects${buildQuery(params)}`, {
        headers: adminHeaders(),
      });
    },
    get(id: string): Promise<Project> {
      return request<Project>(`/projects/${id}`, { headers: adminHeaders() });
    },
    create(data: CreateProjectInput): Promise<Project> {
      return request<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: adminHeaders(),
      });
    },
    update(id: string, data: UpdateProjectInput): Promise<Project> {
      return request<Project>(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: adminHeaders(),
      });
    },
    deactivate(id: string): Promise<{ estado: 'CERRADO' }> {
      return request<{ estado: 'CERRADO' }>(`/projects/${id}`, {
        method: 'DELETE',
        headers: adminHeaders(),
      });
    },
    /** change-70: `ACTIVO → CERRADO` (`POST /projects/:id/close`, `proyecto.editar`). */
    close(id: string): Promise<Project> {
      return request<Project>(`/projects/${id}/close`, {
        method: 'POST',
        headers: adminHeaders(),
      });
    },
  },

  units: {
    /** change-70: consulta paginada del catálogo (`GET /units`, `catalogo.ver`). */
    list(query: CatalogUnitsQuery): Promise<Paginated<CatalogUnit>> {
      return request<Paginated<CatalogUnit>>(`/units${buildQuery({ ...query })}`, {
        headers: adminHeaders(),
      });
    },
    /** HU-A06 (`PATCH /units/:id/price`, `catalogo.editar`): pesos enteros. */
    adjustPrice(id: string, precioLista: number): Promise<CatalogUnit> {
      return request<CatalogUnit>(`/units/${id}/price`, {
        method: 'PATCH',
        body: JSON.stringify({ precioLista }),
        headers: adminHeaders(),
      });
    },
    catalog: {
      template(): Promise<Blob> {
        return requestBlob('/units/catalog/template');
      },
      validate(proyectoId: string, archivo: File): Promise<CatalogValidation> {
        const form = new FormData();
        form.append('archivo', archivo);
        form.append('proyectoId', proyectoId);
        return requestForm<CatalogValidation>('/units/catalog/validate', form, adminHeaders());
      },
      confirm(input: ConfirmCatalogInput): Promise<ConfirmCatalogResult> {
        return request<ConfirmCatalogResult>('/units/catalog/confirm', {
          method: 'POST',
          body: JSON.stringify(input),
          headers: adminHeaders(),
        });
      },
    },
  },

  separations: {
    /** `incluirNoDisponibles` (fix-50 · H-08): también las separadas/vendidas, para marcarlas. */
    availableUnits(proyectoId: string, opciones?: { incluirNoDisponibles?: boolean }): Promise<AvailableUnit[]> {
      const incluirNoDisponibles = opciones?.incluirNoDisponibles ? 'true' : undefined;
      return request<AvailableUnit[]>(
        `/separations/available-units${buildQuery({ proyectoId, incluirNoDisponibles })}`,
        { headers: authorizationHeaders() },
      );
    },
    create(input: CreateSeparationInput): Promise<SeparationDraftResponse> {
      return request<SeparationDraftResponse>('/separations', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: authorizationHeaders(),
      });
    },
    setApplicants(id: string, separantes: ApplicantInput[]): Promise<ApplicantRecord[]> {
      return request<ApplicantRecord[]>(`/separations/${id}/separantes`, {
        method: 'PUT',
        body: JSON.stringify({ separantes }),
        headers: authorizationHeaders(),
      });
    },
    authorize(id: string): Promise<AuthorizationResult> {
      return request<AuthorizationResult>(`/separations/${id}/autorizacion`, {
        method: 'POST',
        headers: authorizationHeaders(),
      });
    },
    /** Detalle de una separación propia (feat-28); el flujo lo usa para retomarla (fix-50). */
    getOwn(id: string): Promise<OwnSeparationDetail> {
      return request<OwnSeparationDetail>(`/separations/mine/${id}`, { headers: authorizationHeaders() });
    },
    /** Ficha de una unidad con su precio de lista (`GET /separations/units/:id`). */
    unit(id: string): Promise<AvailableUnit> {
      return request<AvailableUnit>(`/separations/units/${id}`, { headers: authorizationHeaders() });
    },
    getConditions(id: string): Promise<ConditionsResult> {
      return request<ConditionsResult>(`/separations/${id}/condiciones`, { headers: authorizationHeaders() });
    },
    /** Persiste las condiciones del borrador (fix-50: antes de las cuotas). */
    setConditions(id: string, condiciones: ConditionsInput): Promise<ConditionsResult> {
      return request<ConditionsResult>(`/separations/${id}/condiciones`, {
        method: 'PUT',
        body: JSON.stringify(condiciones),
        headers: authorizationHeaders(),
      });
    },
    getInstallments(id: string): Promise<InstallmentsResult> {
      return request<InstallmentsResult>(`/separations/${id}/cuotas`, {
        headers: authorizationHeaders(),
      });
    },
    setInstallments(id: string, cuotas: InstallmentInput[]): Promise<InstallmentsResult> {
      return request<InstallmentsResult>(`/separations/${id}/cuotas`, {
        method: 'PUT',
        body: JSON.stringify({ cuotas }),
        headers: authorizationHeaders(),
      });
    },
    /** feat-67: `BORRADOR → DESCARTADA`; libera la unidad y sus adicionales. */
    discard(id: string): Promise<DescarteSeparacion> {
      return request<DescarteSeparacion>(`/separations/${encodeURIComponent(id)}/descartar`, {
        method: 'POST',
        headers: authorizationHeaders(),
      });
    },
    /**
     * feat-67: `GENERADA|CORREGIDA → ANULADA` con motivo. `inmobiliariaId` (consulta administrativa):
     * inmobiliaria de la separación como destino (`X-Inmobiliaria-Id`, ADR-018); el back solo lo acepta
     * con `plataforma.gestionar_inmobiliarias`.
     */
    cancel(id: string, motivo: string, inmobiliariaId?: string): Promise<AnulacionSeparacion> {
      return request<AnulacionSeparacion>(`/separations/${encodeURIComponent(id)}/anular`, {
        method: 'POST',
        body: JSON.stringify({ motivo }),
        headers: {
          ...(authorizationHeaders() as Record<string, string>),
          ...(inmobiliariaId ? { [ENCABEZADO_INMOBILIARIA]: inmobiliariaId } : {}),
        },
      });
    },
  },

  paymentPlans: {
    /** Preview oficial del plan: lo calcula el back (`POST /payment-plans/calculate`). */
    calculate(input: CalculatePlanInput): Promise<PaymentPlanPreview> {
      return request<PaymentPlanPreview>('/payment-plans/calculate', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: authorizationHeaders(),
      });
    },
  },

  /** Consultas de separaciones (HU-C08 · feat-28, HU-A05 · feat-29). El alcance lo decide el servidor. */
  separationQueries: {
    mine(params: ConsultaPropiasParams): Promise<Paginated<SeparacionResumen>> {
      return request<Paginated<SeparacionResumen>>(`/separations/mine${buildQuery(comoQuery(params))}`, {
        headers: authorizationHeaders(),
      });
    },
    mineDetail(id: string): Promise<SeparacionDetalle> {
      return request<SeparacionDetalle>(`/separations/mine/${encodeURIComponent(id)}`, {
        headers: authorizationHeaders(),
      });
    },
    adminList(params: ConsultaAdminParams): Promise<Paginated<SeparacionResumen>> {
      return request<Paginated<SeparacionResumen>>(`/separations${buildQuery(comoQuery(params))}`, {
        headers: authorizationHeaders(),
      });
    },
    adminDetail(id: string): Promise<SeparacionDetalle> {
      return request<SeparacionDetalle>(`/separations/${encodeURIComponent(id)}/detalle`, {
        headers: authorizationHeaders(),
      });
    },
  },

  /**
   * Documentos emitidos (HU-C05 · feat-26): generación del plan (fix-50), historial, descarga, enlace,
   * integridad, corrección (feat-42) y envío por correo (feat-31).
   */
  documents: {
    /** Genera el PDF del plan (feat-26 · `contrato-api.md` §1): 422 `PLAN_NO_VALIDO`, 409 `SEPARACION_YA_GENERADA`. */
    generatePaymentPlan(input: GeneratePlanInput): Promise<IssuedDocument> {
      return request<IssuedDocument>('/documents/plan-pagos', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: authorizationHeaders(),
      });
    },
    list(separacionId: string): Promise<DocumentoSeparacion[]> {
      return request<DocumentoSeparacion[]>(`/documents${buildQuery({ separacionId })}`, {
        headers: authorizationHeaders(),
      });
    },
    download(versionId: string): Promise<ArchivoDescargado> {
      return requestArchivo(`/documents/versions/${encodeURIComponent(versionId)}/descarga`);
    },
    link(versionId: string): Promise<EnlaceDescarga> {
      return request<EnlaceDescarga>(`/documents/versions/${encodeURIComponent(versionId)}/enlace`, {
        method: 'POST',
        headers: authorizationHeaders(),
      });
    },
    integrity(versionId: string): Promise<ResultadoIntegridad> {
      return request<ResultadoIntegridad>(
        `/documents/versions/${encodeURIComponent(versionId)}/integridad`,
        { headers: authorizationHeaders() },
      );
    },
    correct(documentoId: string): Promise<CorreccionAbierta> {
      return request<CorreccionAbierta>(`/documents/${encodeURIComponent(documentoId)}/correccion`, {
        method: 'POST',
        headers: authorizationHeaders(),
      });
    },
    /** Envía la versión vigente a los separantes (feat-31 · `contrato-api.md`); sin cuerpo. */
    sendByEmail(versionId: string): Promise<EnvioCorreo> {
      return request<EnvioCorreo>(`/documents/versions/${encodeURIComponent(versionId)}/envio`, {
        method: 'POST',
        headers: authorizationHeaders(),
      });
    },
  },

  users: {
    /** fix-60 · S-01: administradores de la inmobiliaria (alta por invitación por correo). */
    admins: {
      list(): Promise<AdminUser[]> {
        return request<AdminUser[]>('/users/admins', { headers: adminHeaders() });
      },
      create(data: CreateAdminInput): Promise<AdminUser> {
        return request<AdminUser>('/users/admins', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: adminHeaders(),
        });
      },
      resendInvitation(id: string): Promise<{ message: string }> {
        return request<{ message: string }>(`/users/${id}/resend-invitation`, {
          method: 'POST',
          headers: adminHeaders(),
        });
      },
      deactivate(id: string): Promise<unknown> {
        return request<unknown>(`/users/${id}/deactivate`, { method: 'PATCH', headers: adminHeaders() });
      },
      activate(id: string): Promise<unknown> {
        return request<unknown>(`/users/${id}/activate`, { method: 'PATCH', headers: adminHeaders() });
      },
    },
    advisors: {
      list(): Promise<Advisor[]> {
        return request<Advisor[]>('/users/advisors', { headers: adminHeaders() });
      },
      create(data: CreateAdvisorInput): Promise<Advisor> {
        return request<Advisor>('/users/advisors', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: adminHeaders(),
        });
      },
      assignProjects(id: string, proyectoIds: string[]): Promise<{ message: string }> {
        return request<{ message: string }>(`/users/${id}/projects`, {
          method: 'PATCH',
          body: JSON.stringify({ proyectoIds }),
          headers: adminHeaders(),
        });
      },
      resendInvitation(id: string): Promise<{ message: string }> {
        return request<{ message: string }>(`/users/${id}/resend-invitation`, {
          method: 'POST',
          headers: adminHeaders(),
        });
      },
      deactivate(id: string): Promise<{ message: string }> {
        return request<{ message: string }>(`/users/${id}/deactivate`, {
          method: 'PATCH',
          headers: adminHeaders(),
        });
      },
      activate(id: string): Promise<{ message: string }> {
        return request<{ message: string }>(`/users/${id}/activate`, {
          method: 'PATCH',
          headers: adminHeaders(),
        });
      },
    },
  },
};

async function requestForm<T>(
  path: string,
  form: FormData,
  headers: HeadersInit = authorizationHeaders(),
  method: 'POST' | 'PUT' = 'POST',
): Promise<T> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    method,
    body: form,
    headers,
  });
  const contentType = response.headers.get('content-type');
  const body = contentType?.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : 'No fue posible completar la solicitud.';
    throw new ApiError(response.status, message, body);
  }
  return body as T;
}

async function requestBlob(path: string): Promise<Blob> {
  const response = await fetch(`${API_PREFIX}${path}`, { headers: authorizationHeaders() });
  if (!response.ok) {
    throw new ApiError(response.status, 'No fue posible descargar el archivo.');
  }
  return response.blob();
}

/** Descarga binaria con sesión: devuelve el archivo, su nombre y la huella que declara el back. */
async function requestArchivo(path: string): Promise<ArchivoDescargado> {
  const response = await fetch(`${API_PREFIX}${path}`, { headers: authorizationHeaders() });
  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    const body: unknown = contentType?.includes('application/json') ? await response.json() : null;
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : 'No fue posible descargar el archivo.';
    throw new ApiError(response.status, message, body);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const nombre = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  return {
    blob: await response.blob(),
    nombreArchivo: nombre ?? 'documento.pdf',
    huella: response.headers.get('x-documento-sha256')?.toLowerCase() ?? null,
  };
}

/** Parámetros de consulta como mapa plano para `buildQuery` (que omite los vacíos). */
function comoQuery(
  params: ConsultaPropiasParams | ConsultaAdminParams,
): Record<string, string | number | undefined> {
  return Object.fromEntries(Object.entries(params)) as Record<string, string | number | undefined>;
}

function buildQuery(params?: Record<string, number | string | undefined>): string {
  if (!params) {
    return '';
  }
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

function authorizationHeaders(): HeadersInit {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/** Encabezados de las llamadas administrativas que el back acepta con inmobiliaria destino (ADR-018). */
function adminHeaders(): HeadersInit {
  const base = authorizationHeaders() as Record<string, string>;
  const destino = getInmobiliariaActiva();
  return destino ? { ...base, [ENCABEZADO_INMOBILIARIA]: destino } : base;
}
